import * as React from "react";

// Sticky address-labeled table of contents, ported from
// design-reference/blog-gc-post.html's `.toc` pattern. Scans the rendered
// post body for `h2[id]` (ids come from rehype-slug in the publish-time
// compile step, see apps/api/src/services/mdx-compile.ts) and tracks scroll
// position the same way the reference's inline <script> did.
//
// The reading-progress-bar-as-instruction-pointer (`#progress-rail` /
// `#progress-bar` / `#ip-label`) now lives in ScrollProgress.tsx, mounted
// once in __root.tsx so it shows on every route, not just posts.

interface Heading {
  id: string;
  text: string;
}

export function usePostHeadings(
  containerRef: React.RefObject<HTMLElement | null>,
  ready: boolean,
): Heading[] {
  const [headings, setHeadings] = React.useState<Heading[]>([]);

  React.useEffect(() => {
    if (!ready) {
      setHeadings([]);
      return;
    }
    const el = containerRef.current;
    if (!el) return;
    // The MDX content mounts asynchronously (useCompiledMdx's effect), so
    // give React a tick to paint before reading the DOM.
    const id = requestAnimationFrame(() => {
      const nodes = Array.from(el.querySelectorAll<HTMLHeadingElement>("h2[id]"));
      setHeadings(nodes.map((n) => ({ id: n.id, text: n.textContent ?? "" })));
    });
    return () => cancelAnimationFrame(id);
  }, [containerRef, ready]);

  return headings;
}

export function PostReadingChrome({ headings }: { headings: Heading[] }) {
  const [activeId, setActiveId] = React.useState<string | null>(null);

  React.useEffect(() => {
    function onScroll() {
      let current: string | null = headings[0]?.id ?? null;
      for (const h of headings) {
        const el = document.getElementById(h.id);
        if (el && window.scrollY >= el.offsetTop - 100) current = h.id;
      }
      setActiveId(current);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <aside className="post-toc-aside">
      <div className="toc">
        <p className="toc-label">// sections</p>
        {headings.map((h, i) => (
          <a key={h.id} href={`#${h.id}`} className={h.id === activeId ? "active" : ""}>
            0x{String(i).padStart(2, "0")} &mdash; {h.text}
          </a>
        ))}
      </div>
    </aside>
  );
}
